/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {globalGroupSelector} from '@ion/applications/src/store/selectors/global-group';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink, CRUDPermissions, Entity, EntityStatus} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {GroupChooserButton} from '@ion/dls/src/components/buttons/GroupChooserButton';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {getAccountLink} from '@ion/dls/src/editors/AdminGroupEditorUtils';
import {AuditDialog} from '@ion/dls/src/editors/audit/AuditDialog';
import {ViewAuditChangesDialog} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {DLSTheme} from '@ion/dls/src/types/types';
import useAccountInHierarchy from '../../../hooks/useAccountInHierarchy';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import EditGroupEditor from './EditGroupEditor';
import {auditAdminGroup, editAdminGroup, showAuditChangesDialog} from './state';

export interface AccountCellType {
  item: GridResult;
  onSelectAccount?: (value: AccountLink | null) => void;
  refreshInitialization?: Function;
  accountsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
  isCtcAdmin?: boolean;
  isAccountGrid?: boolean;
  isParentAccount?: boolean;
  includeAccountLink?: boolean;
  isChipView?: boolean;
  includeApplications?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  groupChooser : {
    alignItems : 'center',
    display    : 'flex'
  },
  groupName : {
    lineHeight : '24px'
  },
  groupChooserButton : {
    cursor : 'pointer'
  }
}));

/**
 * Defines the AccountCell component. This component is used to display
 * the account name with icon and on click of icon account gets choosed
 *
 * ```typescript
 *
 *   import AccountCell from './AccountCell';
 *
 *   <IonGrid  columnComponents={{
 *     accountName : AccountCell
 *   }} />
 *
 * ```
 */
export const AccountCell = React.memo(({
  item,
  onSelectAccount,
  refreshInitialization,
  accountsPermissions,
  isCtcAdmin,
  Features,
  isAccountGrid = false,
  isParentAccount = false,
  includeAccountLink = true,
  isChipView = false,
  includeApplications = false
}: AccountCellType) => {
  const classes          = useStyles();
  const dispatch         = useDispatch();
  const {data}           = item;
  const accountHref      = getAccountLink(data, isParentAccount, isAccountGrid);
  const accountId        = entityId(accountHref);
  const globalAccountId  = entityId(useSelector(globalGroupSelector));
  const isCurrentAccount = accountId === globalAccountId;
  const {account}        = useAuthenticatedUser();

  const hasCustomVisionEditPermission = Features?.hasPermission(Entity.customvisionsettings, Operation.Update) || false;

  const accountTitle = (isAccountGrid || isParentAccount) ? item.getValue() : data?.account?.title;

  const {
    isEditing: showAuditGroup,
    selected: auditGroup
  } = useSelector(({adminGroups: {audit}}) => audit);
  const {
    auditRecord: selectedAuditRecord,
    showChangesDialog: shouldShowAuditChangesDialog
  } = useSelector(({adminGroups: {audit}}) => audit);
  const {
    isEditing: showEditGroup,
    isViewMode: showViewGroup
  } = useSelector(({adminGroups: {edit}}) => edit);

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog]       = useState<boolean>(false);

  const {getAccount} = useAccountInHierarchy();

  const onAccountClick = useCallback(() => {
    const selectedAccount = getAccount(accountId);
    onSelectAccount?.(selectedAccount);
  }, []);

  const onChooserClick = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const onChangeAccount = useCallback(() => {
    setShowConfirmDialog(false);
    onAccountClick();
  }, []);

  const onDismiss = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const onAuditClose = useCallback(() => {
    dispatch(auditAdminGroup(null, false));
  }, []);

  const onViewAuditChanges = useCallback((auditRecord, show = false) => {
    dispatch(showAuditChangesDialog(auditRecord, show));
  }, []);

  const onViewAuditChangesClose = useCallback(() => {
    dispatch(showAuditChangesDialog(null, false));
  }, []);

  const getTitle = useCallback(() => {
    const {status} = getAccount(accountId) || {};
    const options  = {account: accountTitle};
    if (!isCurrentAccount) {
      const entry = status === EntityStatus.Enabled ? 'message' : 'disableMessage';
      return translate(`common:groupChooser.${entry}`, options);
    } else {
      return translate('common:groupChooser.currentAccountMessage', options);
    }
  }, [data, isCurrentAccount, accountId, accountTitle]);

  const onAccountLinkClick = useCallback(() => {
    setShowEditDialog(true);
    dispatch(editAdminGroup({
      account    : accountId,
      show       : true,
      isViewMode : true
    }) as any);
  }, [accountId]);

  useEffect(() => {
    if (!showViewGroup && !showEditGroup) {
      setShowEditDialog(false);
    }
  }, [showViewGroup, showEditGroup]);

  if (item) {
    return (
      <>
        <div className={classes.groupChooser}>
          <div className={classes.groupChooserButton}>
            <GroupChooserButton onClick={onChooserClick} />
          </div>
          {includeAccountLink
            ? <LinkCell data={item} onClick={onAccountLinkClick} />
            : <span className={classes.groupName}>{accountTitle}</span>
          }
        </div>
        {showConfirmDialog && (
          <UserNotificationDialog message={getTitle()}
            title={translate('common:groupChooser.changeAccount')}
            actionLabel={translate('common:groupChooser.yesContinue')}
            dismissLabel={translate('common:groupChooser.neverMind')}
            onAction={(isCurrentAccount || getAccount(accountId)?.status !== EntityStatus.Enabled)
              ? null : onChangeAccount}
            onDismiss={onDismiss}
            capitalize
            show />
        )}
        {(showViewGroup || showEditGroup) && showEditDialog && (
          <EditGroupEditor isCtcAdmin={isCtcAdmin}
            isChipView={isChipView}
            isViewDialog={showViewGroup}
            accountsPermissions={accountsPermissions}
            hasCustomVisionPermission={hasCustomVisionEditPermission}
            defaultAccount={account}
            refreshInitialization={refreshInitialization}
            includeApplications={includeApplications} />
        )}
        {(showAuditGroup && showEditDialog) && (
          <AuditDialog item={auditGroup}
            entity={Entity.account}
            onCloseDialog={onAuditClose}
            rowMenusEnabled={isCtcAdmin}
            showDifferences={isCtcAdmin}
            onViewAuditChanges={onViewAuditChanges} />
        )}
        {shouldShowAuditChangesDialog && selectedAuditRecord && showEditDialog && (
          <ViewAuditChangesDialog auditRecord={selectedAuditRecord}
            onCloseDialog={onViewAuditChangesClose}
            entity={Entity.account} />
        )}
      </>
    );
  }
  return null;
});

export default AccountCell;
